import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@hey-car/ui';
import { Waypoint } from 'react-waypoint';
import { intlShape } from 'react-intl';

import { HtmlHeader } from 'app/shared/components/HtmlHeader/HtmlHeader';
import { ConnectedTopNavBar as TopNavBar } from 'app/dealer/shared/components/TopNavBar/ConnectedTopNavBar';
import { FixedPositionInjector } from 'app/shared/components/FixedPositionInjector/FixedPositionInjector';
import { ScrollTopInjector } from 'app/shared/components/ScrollTopInjector/ScrollTopInjector';
import { Footer } from 'app/shared/components/Footer/Footer';
import { menuItems } from 'app/dealer/menuitems';

import {
  NavigationMenuSt,
  DealerPageContainerSt,
  BackToTopSt,
  ScrollUpButtonWrapperSt,
} from './DealerPage.css';
import { CookieBanner } from 'app/shared/components/CookieBanner/CookieBanner';

// layout for logged in dealer page
const DealerPage = ({
  intl,
  messages,
  modal,
  network,
  showMenu,
  pathname,
  hasOverlay,
  title,
  titleTo,
  topNavBarTarget,
  footerTarget,
  shouldShowBottomPrompt,
  showAvatar,
  hideTagLine,
  isLoggedIn,
  children,
}) => {
  const [dockBackToTopInFooter, setDockBackToTopInFooter] = useState(false);

  const handleSetBackToTopInFooter = useCallback(
    /* istanbul ignore next */ () => setDockBackToTopInFooter(true),
    [setDockBackToTopInFooter],
  );

  const handleSetBackToTopOutsideFooter = useCallback(
    /* istanbul ignore next */ () => setDockBackToTopInFooter(false),
    [setDockBackToTopInFooter],
  );

  return (
    <DealerPageContainerSt
      showMenu={showMenu}
      shouldShowBottomPrompt={shouldShowBottomPrompt}
    >
      {!!modal && modal}
      <HtmlHeader
        title={intl.formatMessage(messages.pageTitle)}
        description={intl.formatMessage(messages.metaDescription)}
      />
      <TopNavBar
        target={topNavBarTarget}
        showMenu={showMenu}
        network={network}
        pathname={pathname}
        title={title}
        titleTo={titleTo}
        showAvatar={showAvatar}
        hideTagLine={hideTagLine}
      />
      {showMenu && (
        <NavigationMenuSt menuItems={menuItems(pathname, { network })} />
      )}
      <FixedPositionInjector hasOverlay={hasOverlay}>
        <ScrollTopInjector>
          {children}
          <Waypoint
            onEnter={handleSetBackToTopInFooter}
            onLeave={handleSetBackToTopOutsideFooter}
            bottomOffset={96}
          >
            <div>
              <Footer target={footerTarget} isLoggedIn={isLoggedIn} />
            </div>
          </Waypoint>
        </ScrollTopInjector>
      </FixedPositionInjector>
      <ScrollUpButtonWrapperSt
        ContainerClassName="backToTopButtonContainer"
        TransitionClassName="backToTopButtonTransition"
        StopPosition={0}
        ShowAtPosition={150}
        EasingType="easeOutCubic"
        AnimationDuration={500}
      >
        <BackToTopSt dockBackToTopInFooter={dockBackToTopInFooter ? 1 : 0}>
          <Icon icon="arrow-up-short-short" color="#ffffff" />
        </BackToTopSt>
      </ScrollUpButtonWrapperSt>
      <CookieBanner target={topNavBarTarget} />
    </DealerPageContainerSt>
  );
};

const requiredProps = {
  // properties
  intl: intlShape.isRequired,

  messages: PropTypes.shape({
    pageTitle: PropTypes.shape({
      id: PropTypes.string,
      defaultMessage: PropTypes.string,
    }).isRequired,
    metaDescription: PropTypes.shape({
      id: PropTypes.string,
      defaultMessage: PropTypes.string,
    }).isRequired,
  }),
  topNavBarTarget: PropTypes.string.isRequired,
  footerTarget: PropTypes.string.isRequired,
  showMenu: PropTypes.bool.isRequired,
};
const optionalProps = {
  // properties
  branches: PropTypes.array,
  dealerListingsCount: PropTypes.number,
  hasOverlay: PropTypes.bool,
  modal: PropTypes.node,
  title: PropTypes.object,
  titleTo: PropTypes.string,
  shouldShowBottomPrompt: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  showAvatar: PropTypes.bool,
  hideTagLine: PropTypes.bool,
  network: PropTypes.object,
};

DealerPage.propTypes = {
  ...requiredProps,
  ...optionalProps,
};

DealerPage.defaultProps = {
  branches: [],
  modal: null,
  title: null,
  titleTo: null,
  dealerListingsCount: 0,
  hasOverlay: false,
  shouldShowBottomPrompt: false,
  showAvatar: false,
  hideTagLine: false,
  isLoggedIn: false,
  network: {},
};

export default DealerPage;
